/**
 * TradeFinancialsBlock — consolidated amber cell holding the per-trade
 * financials (ACV / Retail / Plan) as stacked sub-rows.
 *
 * Replaces the former six separate columns (T1/T2 × ACV/Retail/Plan). The T1
 * sub-row always shows; the T2 sub-row appears automatically once any trade-2
 * field has data (mirrors TradeInfoBlock's up-to-2-trades handling), or can be
 * revealed manually via "+ T2" to enter a second trade's financials by hand.
 */

import React, { useState } from 'react';
import { CurrencyInput } from './CurrencyInput';
import { CurrencyOrTextInput } from './CurrencyOrTextInput';
import { PlanSelect } from './PlanSelect';
import { NutSheetRowDraft } from '../../types/nutSheet';
import { numCell } from './cellStyles';

interface TradeFinancialsBlockProps {
  row: NutSheetRowDraft;
  locationId: number;
  onChange: (patch: Partial<NutSheetRowDraft>) => void;
  saving: boolean;
}

/**
 * Column template for the Trade $ cell — shared with the table header
 * (NutSheetTable) so the ACV / Retail / Plan header labels align exactly
 * over the input columns.
 */
export const tradeDollarGrid = 'grid grid-cols-[1.5rem_1fr_1fr_4rem] gap-1';

const gridCols = `${tradeDollarGrid} items-center`;

export const TradeFinancialsBlock: React.FC<TradeFinancialsBlockProps> = ({
  row,
  locationId,
  onChange,
  saving,
}) => {
  const hasTrade2 = [
    row.trade2StockNumber, row.trade2Year, row.trade2Make, row.trade2Model,
    row.trade2Miles, row.trade2Acv, row.trade2Retail, row.t2Plan,
  ].some((v) => v !== null && v !== undefined && v !== '');
  const [showT2, setShowT2] = useState(false);
  const t2Visible = hasTrade2 || showT2;

  return (
    <div className="min-w-[250px] space-y-0.5">
      {/* Trade 1 — ACV/Retail/Plan labels live in the table column header */}
      <div className={gridCols}>
        <span className="text-[10px] font-semibold text-amber-800">T1</span>
        <CurrencyInput value={row.acv} onChange={(v) => onChange({ acv: v })} className={numCell} />
        <CurrencyOrTextInput value={row.retail} onChange={(v) => onChange({ retail: v })} className={numCell} />
        <PlanSelect locationId={locationId} value={row.t1Plan} onChange={(v) => onChange({ t1Plan: v })} disabled={saving} />
      </div>

      {/* Trade 2 — only when a second trade exists (or revealed manually) */}
      {t2Visible ? (
        <div className={gridCols}>
          <span className="text-[10px] font-semibold text-amber-800">T2</span>
          <CurrencyInput value={row.trade2Acv} onChange={(v) => onChange({ trade2Acv: v })} className={numCell} />
          <CurrencyOrTextInput value={row.trade2Retail} onChange={(v) => onChange({ trade2Retail: v })} className={numCell} />
          <PlanSelect locationId={locationId} value={row.t2Plan} onChange={(v) => onChange({ t2Plan: v })} disabled={saving} />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowT2(true)}
          disabled={saving}
          className="pl-[1.5rem] text-[10px] text-amber-700 hover:text-amber-900 disabled:opacity-50"
        >
          + T2
        </button>
      )}
    </div>
  );
};
